import React, { useMemo } from "react";
import "./displayIcons.scss";
interface ClockDurationIconProps {
  minutes: number; // Total minutes (can be > 60)
  size?: number; // SVG size
  strokeWidth?: number;
  color?: "blue" | "brown" | "green" | "red" | "grey"; // Default stroke color
  onClick?: any;
  showZero?: boolean;
  css?: React.CSSProperties;
}

const ClockDurationIcon: React.FC<ClockDurationIconProps> = ({
  minutes,
  size = 30,
  strokeWidth = 6,
  onClick,
  showZero = true,
  color = "brown",
  css,
}) => {
  const radius = (size - strokeWidth) / 2;

  const circumference = 2 * Math.PI * radius;
  const arcColor = useMemo(() => {
    if (color === "blue") return "var(--color__blue500)";
    else if (color === "grey") return "var(--color__grey400)";
    else if (color === "red") return "var(--color__red500)";
    else if (color === "green") return "var(--color__green500)";
    else return "var(--color__brown300)";
  }, [color]);

  const { fullHours, minuteProgress } = useMemo(() => {
    const fullHours = Math.floor(minutes / 60);
    const minuteProgress = Math.min(minutes % 60, 60) / 60;
    return { fullHours, minuteProgress };
  }, [minutes]);

  const offset = circumference * (1 - minuteProgress);

  const isMoreThanOneHour = fullHours > 0;

  const backgroundColor = useMemo(
    () =>
      minuteProgress === 0 && isMoreThanOneHour
        ? arcColor
        : "var(--color__white)",
    [isMoreThanOneHour, minuteProgress, arcColor],
  );

  return (
    <div
      className={`clock-duration-icon icon-big ${color}`}
      style={
        {
          width: size,
          height: size,
          ...css,
          "--icon-border-color": arcColor,
        } as React.CSSProperties
      }
      onClick={onClick}
    >
      <svg
        className={`clock-duration-icon-svg ${
          isMoreThanOneHour ? "long-duration" : ""
        }`}
        width={size}
        height={size}
      >
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          className={`clock-duration-icon-arc`}
          r={radius}
          stroke={arcColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      {(isMoreThanOneHour || (showZero && minutes > 0)) && (
        <p className="clock-duration-icon-text p_small">
          {fullHours}:
          {Math.floor(minuteProgress * 60)
            .toString()
            .padStart(2, "0")}
        </p>
      )}
    </div>
  );
};

export default ClockDurationIcon;
